/**
 * The frame around demo mode.
 *
 * Demo mode exists so that a screenshot never has to be taken by making the
 * live bridge lie. That trade is only safe if the two can never be confused,
 * so the marking is not a caption that can scroll away — it is structural:
 *
 *   - the URL says `/demo`;
 *   - a banner is pinned to the top of the screen and stays there while the
 *     page scrolls, so it is in every screenshot of every part of it;
 *   - a hazard-striped overlay covers the whole viewport, including the
 *     navigation, and cannot be scrolled out of shot either;
 *   - the document title is prefixed, so a browser tab and a window capture
 *     say it too;
 *   - and every readout underneath carries the FIXTURE provenance chip that
 *     the live screens use to mark invented data, because that is what it is.
 *
 * Yellow, not red. Red on this product means a confirmed fault, and a demo is
 * not a fault.
 *
 * Leaving is one action, and it is the first control in the banner.
 */
import { useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

export function DemoFrame({ children }: { children: ReactNode }) {
  useEffect(() => {
    const previous = document.title;
    document.title = `DEMO · SIMULATED — ${previous}`;
    document.body.dataset.demo = 'true';
    return () => {
      document.title = previous;
      delete document.body.dataset.demo;
    };
  }, []);

  return (
    <div className="demo">
      <div className="demo__banner">
        <span className="demo__mark">DEMO · SIMULATED DATA</span>
        <p className="demo__note">
          A simulated fleet, for screenshots and walkthroughs. No project, person, lease or number
          below is real, nothing here reads or writes the live bridge, and no lease is taken.
        </p>
        <Link className="btn btn--sm demo__exit" to="/">
          Leave demo
        </Link>
      </div>
      {children}
      {/* Fixed, so it covers the navigation as well and cannot be scrolled
          out of a screenshot. Inert: it never intercepts a click. */}
      <div className="demo__hazard" aria-hidden="true" />
      <span className="demo__corner" aria-hidden="true">
        SIMULATED
      </span>
    </div>
  );
}
